import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { StyleObj } from '../../@types';
import { CasinoTransactionRow, CasinoTransactionsQueryParams } from '../../@types/casino';
import { prepareCasinoTransactionFilters } from '../../helpers/casino';
import { createNumberColumn } from '../../helpers/columns';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { useCasinoTransactions } from '../../queries/casino';
import { createColumn } from '../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../molecules/DateRangeSelect';
import TableTemplate from '../templates/TableTemplate';

const DEFAULT_SORT_COLUMN = 'createdAt';
const DEFAULT_SORT_ORDER = 'desc';

const createDefaultDateRange = (): DayjsTimestamp => ({
  fromTimestamp: dayjs().startOf('month'),
  toTimestamp: dayjs(),
});

const createDefaultQueryParams = (date: DayjsTimestamp): CasinoTransactionsQueryParams => ({
  page: 1,
  limit: 25,
  dateFrom: date.fromTimestamp.valueOf(),
  dateTo: date.toTimestamp.valueOf(),
  sortColumn: DEFAULT_SORT_COLUMN,
  sortOrder: DEFAULT_SORT_ORDER,
});

const EMPTY_TRANSACTION_FIELDS: Array<Extract<keyof CasinoTransactionsQueryParams, string>> = [
  'roundId',
  'gameName',
  'transactionAmount',
  'providerName',
  'transactionType',
];

const EMPTY_TRANSACTION_ARRAY_FIELDS: Array<Extract<keyof CasinoTransactionsQueryParams, string>> = [
  'managerIds',
  'agentIds',
  'playerIds',
];

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const CasinoTransactionsPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>(createDefaultDateRange);
  const { queryParams, paginationModel, updateQueryParams, changeQuery, handleSort, changeDateRange } =
    useCasinoTableQuery<CasinoTransactionsQueryParams>({
      defaultQuery: () => createDefaultQueryParams(date),
      emptyFields: EMPTY_TRANSACTION_FIELDS,
      emptyArrayFields: EMPTY_TRANSACTION_ARRAY_FIELDS,
      sortDefaults: {
        sortColumn: 'sortColumn',
        sortOrder: 'sortOrder',
        defaultColumn: DEFAULT_SORT_COLUMN,
        defaultOrder: DEFAULT_SORT_ORDER,
      },
      prepareQuery: prepareCasinoTransactionFilters,
    });
  const { data: casinoTransactionsData, isFetching, isLoading } = useCasinoTransactions(queryParams);

  const columns = useMemo<GridColDef<CasinoTransactionRow>[]>(
    () => [
      createColumn('createdAt', 'Created At', {
        minWidth: 180,
        valueGetter: (_value, row, _column, _apiRef) =>
          row.createdAt ? dayjs(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '',
      }),
      createColumn('username', 'Username', {
        minWidth: 160,
      }),
      createColumn('gameName', 'Game Name', {
        minWidth: 220,
        flex: 1,
      }),
      createColumn('providerName', 'Provider', {
        minWidth: 180,
      }),
      createColumn('roundId', 'Round ID', {
        minWidth: 180,
        sortable: false,
      }),
      createColumn('_id', 'Transaction ID', {
        minWidth: 220,
        sortable: false,
      }),
      createNumberColumn('transactionAmount', 'Amount', {
        minWidth: 120,
      }),
      createColumn('transactionType', 'Type', {
        minWidth: 120,
        sortable: false,
      }),
      createColumn('aggregatorName', 'Aggregator', {
        minWidth: 160,
        sortable: false,
      }),
    ],
    []
  );

  const handleDateChange = useCallback(
    (newDate: DayjsTimestamp) => {
      setDate(newDate);
      changeDateRange({
        dateFrom: newDate.fromTimestamp.valueOf(),
        dateTo: newDate.toTimestamp.valueOf(),
      });
    },
    [changeDateRange]
  );

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={casinoTransactionsData?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={casinoTransactionsData?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSort={handleSort}
        changeQuery={changeQuery}
        showFilter
        paginationModel={paginationModel}
        filterType='casino-transactions'
        customTableHeaderContent={
          <DateRangeSelect date={date} onDateChange={handleDateChange} defaultDateRangeOption='custom' />
        }
      />
    </Box>
  );
};

export default CasinoTransactionsPage;
